import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { identifyClarityUser } from "@/lib/clarity";
import { requestInstallPrompt } from "@/lib/installPromptBus";

// Once per user we offer the install sheet right after their first session
// appears. Stored in localStorage so it survives refresh and doesn't replay.
const SIGNUP_PROMPT_FLAG = "atp:signup-prompted";

interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .single();
    setProfile(data);
  };

  useEffect(() => {
    // Set up auth listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          // Use setTimeout to avoid potential deadlock with Supabase client
          setTimeout(() => fetchProfile(session.user.id), 0);
          // Identify user to Clarity by internal ID only (never email)
          identifyClarityUser(session.user.id);
          // First time we see this user this device → offer install. We treat
          // any account < 10 min old as "just signed up" to cover the email
          // confirmation round-trip and OAuth callbacks.
          try {
            const createdAt = new Date(session.user.created_at).getTime();
            const isFresh = Date.now() - createdAt < 10 * 60 * 1000;
            if (isFresh && !localStorage.getItem(SIGNUP_PROMPT_FLAG)) {
              localStorage.setItem(SIGNUP_PROMPT_FLAG, "1");
              setTimeout(() => requestInstallPrompt("signup"), 1200);
            }
          } catch { /* localStorage unavailable */ }
        } else {
          setProfile(null);
        }
        setLoading(false);
      }
    );

    // THEN check existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        identifyClarityUser(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
